/**
 * VersionHistory - Version timeline with download for all drafts
 * 
 * Place at: src/components/overlays/VersionHistory.jsx
 * 
 * Shows a chronological timeline of every version uploaded to a project.
 * Users can expand any version to see details and download it — not just
 * the latest, but every iteration. Owners get an inline upload form.
 * 
 * Used inside ProjectDetailOverlay as a tab.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Download, Upload, ChevronDown, FileText, Clock, Check,
  AlertCircle, Image, Code, Music, Video, Box, Archive, File,
  Palette
} from 'lucide-react';
import { cn, formatRelativeTime, formatDate, formatFileSize } from '../../utils/cn';
import { Button, Badge, Avatar, Spinner } from '../ui';
import { api } from '../../api';
import { API_ORIGIN } from '../../config/apiBase';
import toast from 'react-hot-toast';

// ============================================================================
// FILE ICON RESOLVER
// ============================================================================

function getFileIconComponent(fileName) {
  if (!fileName) return File;
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  
  const map = {
    jpg: Image, jpeg: Image, png: Image, gif: Image, webp: Image, svg: Image, bmp: Image, tiff: Image,
    pdf: FileText, doc: FileText, docx: FileText, txt: FileText, rtf: FileText, md: FileText,
    js: Code, jsx: Code, ts: Code, tsx: Code, py: Code, java: Code, cpp: Code, c: Code, h: Code,
    html: Code, css: Code, json: Code, xml: Code, yaml: Code, yml: Code, rb: Code, php: Code,
    go: Code, rs: Code, swift: Code, sql: Code,
    blend: Box, obj: Box, stl: Box, fbx: Box, step: Box, stp: Box, '3ds': Box, dae: Box,
    mp3: Music, wav: Music, ogg: Music, flac: Music, m4a: Music, aac: Music,
    mp4: Video, mov: Video, avi: Video, mkv: Video, webm: Video,
    zip: Archive, rar: Archive, '7z': Archive, tar: Archive, gz: Archive,
    psd: Palette, ai: Palette, sketch: Palette, fig: Palette, xd: Palette,
  };
  
  return map[ext] || File;
}


// ============================================================================
// SINGLE VERSION ROW
// ============================================================================

function VersionRow({ version, isLatest, isExpanded, onToggle, onDownload }) {
  const FileIcon = getFileIconComponent(version.fileName || version.file_name);
  
  return (
    <div className="relative pl-12 pb-1">
      {/* Timeline dot */}
      <div className={cn(
        'absolute left-3 top-4 w-3.5 h-3.5 rounded-full border-2 z-10',
        isLatest
          ? 'bg-purple-500 border-purple-400 shadow-lg shadow-purple-500/30'
          : 'bg-zinc-800 border-zinc-600'
      )} />
      
      <div
        className={cn(
          'rounded-xl transition-all duration-200 cursor-pointer group',
          isExpanded
            ? 'bg-zinc-800/60 border border-zinc-700/50'
            : 'hover:bg-zinc-800/30'
        )}
        onClick={onToggle}
      >
        {/* Collapsed header */}
        <div className="p-3">
          <div className="flex items-center gap-2">
            <span className={cn(
              'text-xs font-bold tabular-nums',
              isLatest ? 'text-purple-400' : 'text-zinc-500'
            )}>
              v{version.versionNumber || version.version_number}
            </span>
            {isLatest && (
              <span className="text-[10px] font-semibold tracking-wider uppercase px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-300 border border-purple-500/20">
                latest
              </span>
            )}
            <span className="text-xs text-zinc-600 ml-auto">
              {formatRelativeTime(version.createdAt || version.created_at)}
            </span>
            <ChevronDown className={cn(
              'w-3.5 h-3.5 text-zinc-600 transition-transform duration-200',
              isExpanded && 'rotate-180'
            )} />
          </div>
          
          {(version.notes) && (
            <p className={cn(
              'text-sm text-zinc-300 mt-1.5 leading-relaxed',
              !isExpanded && 'line-clamp-1'
            )}>
              {version.notes}
            </p>
          )}
        </div>
        
        {/* Expanded details */}
        {isExpanded && (
          <div className="px-3 pb-3 space-y-3">
            <div className="h-px bg-zinc-700/40" />
            
            {/* File info row */}
            <div className="flex items-center gap-3 p-2.5 rounded-lg bg-zinc-900/50">
              <div className="w-9 h-9 rounded-lg bg-zinc-700/50 flex items-center justify-center flex-shrink-0">
                <FileIcon className="w-4 h-4 text-zinc-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-zinc-200 truncate">
                  {version.fileName || version.file_name}
                </p>
                <p className="text-xs text-zinc-500">
                  {formatFileSize(version.fileSize || version.file_size)}
                  {' · '}
                  {formatDate(version.createdAt || version.created_at)}
                </p>
              </div>
              <Button
                variant="secondary"
                size="sm"
                icon={Download}
                onClick={(e) => {
                  e.stopPropagation();
                  onDownload?.(version);
                }}
              >
                Download
              </Button>
            </div>
            
            {/* Author */}
            {version.author && (
              <div className="flex items-center gap-2">
                <Avatar
                  src={version.author.profilePictureUrl || version.author.profile_picture_url}
                  alt={version.author.username}
                  size="xs"
                />
                <span className="text-xs text-zinc-500">
                  Uploaded by{' '}
                  <span className="text-zinc-300">{version.author.username}</span>
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


// ============================================================================
// UPLOAD FORM
// ============================================================================

function UploadForm({ projectId, nextVersionNumber, onSuccess, onCancel }) {
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  const handleSubmit = async () => {
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('notes', notes.trim());
      
      const response = await api.post(`/projects/${projectId}/versions`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`Version ${nextVersionNumber} uploaded!`);
      onSuccess?.(response.data.version || response.data);
    } catch (err) {
      console.error('Version upload failed:', err);
      toast.error(
        err.response?.data?.error ||
        err.response?.data?.msg ||
        'Failed to upload version'
      );
    } finally {
      setUploading(false);
    }
  };
  
  return (
    <div className="rounded-xl bg-zinc-800/60 border border-zinc-700/50 p-4 space-y-3">
      {/* File picker */}
      <div>
        <label className="block text-xs font-medium text-zinc-400 mb-1.5">File</label>
        <input
          ref={fileInputRef}
          type="file"
          onChange={(e) => setFile(e.target.files[0] || null)}
          className="hidden"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          className={cn(
            'w-full flex items-center gap-2 p-3 rounded-lg border transition-colors text-left',
            file
              ? 'bg-purple-500/5 border-purple-500/20'
              : 'bg-zinc-900/60 border-zinc-700/40 hover:border-purple-500/30'
          )}
        >
          {file ? (
            <>
              <Check className="w-4 h-4 text-purple-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-zinc-200 truncate">{file.name}</p>
                <p className="text-xs text-zinc-500">{formatFileSize(file.size)}</p>
              </div>
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 text-zinc-500" />
              <span className="text-sm text-zinc-400">Choose file...</span>
            </>
          )}
        </button>
      </div>
      
      {/* Notes */}
      <div>
        <label className="block text-xs font-medium text-zinc-400 mb-1.5">What changed?</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full bg-zinc-900/60 border border-zinc-700/40 rounded-lg px-3 py-2 text-sm text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-purple-500/40 focus:ring-1 focus:ring-purple-500/20 resize-none transition-colors"
          rows={2}
          placeholder="Describe what you updated in this version..."
        />
      </div>
      
      {/* Actions */}
      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={uploading}>
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={handleSubmit}
          disabled={!file || uploading}
          loading={uploading}
        >
          Upload v{nextVersionNumber}
        </Button>
      </div>
    </div>
  );
}


// ============================================================================
// MAIN EXPORT: VersionHistory
// ============================================================================

const VersionHistory = ({ projectId, isOwner = false }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  
  // Fetch versions
  useEffect(() => {
    if (!projectId) return;
    
    let cancelled = false;
    
    const fetchVersions = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/projects/${projectId}/versions`);
        if (!cancelled) {
          setVersions(response.data.versions || []);
        }
      } catch (err) {
        console.error('Failed to fetch versions:', err);
        if (!cancelled) {
          setVersions([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    
    fetchVersions();
    return () => { cancelled = true; };
  }, [projectId]);
  
  // Download handler
  const handleDownload = (version) => {
    const url = version.fileUrl || version.file_url;
    if (!url) {
      toast.error('No download URL available');
      return;
    }
    
   // Stored URLs already carry their own path prefix — /api/projects/uploads/…
    // for project files, /uploads/avatars/… for avatars — so they join to the
    // ORIGIN, never to API_BASE. Adding /api here would double it.
    let fullUrl = url.startsWith('http') ? url : API_ORIGIN + url;
    
    fullUrl += (fullUrl.includes('?') ? '&' : '?') + 'download=true';
    
    const a = document.createElement('a');
    a.href = fullUrl;
    a.download = version.fileName || version.file_name || 'download';
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };
  
  // Download all versions
  const handleDownloadAll = async () => {
    for (const v of versions) {
      handleDownload(v);
      await new Promise(r => setTimeout(r, 600));
    }
  };
  
  // After successful upload
  const handleUploadSuccess = (newVersion) => {
    setVersions(prev => [newVersion, ...prev]);
    setShowUpload(false);
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="md" />
      </div>
    );
  }
  
  // Empty state
  if (versions.length === 0 && !showUpload) {
    return (
      <div className="space-y-3">
        {isOwner && (
          <button
            onClick={() => setShowUpload(true)}
            className="w-full flex items-center justify-center gap-2 p-3 rounded-xl border-2 border-dashed border-zinc-700/60 hover:border-purple-500/40 hover:bg-purple-500/5 text-zinc-400 hover:text-purple-300 transition-all text-sm font-medium"
          >
            <Upload className="w-4 h-4" />
            Upload First Version
          </button>
        )}
        
        {!isOwner && (
          <div className="text-center py-12">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-zinc-800/50 flex items-center justify-center mb-4">
              <FileText className="w-6 h-6 text-zinc-600" />
            </div>
            <p className="text-zinc-400 text-sm font-medium mb-1">No versions yet</p>
            <p className="text-zinc-600 text-xs">
              The creator hasn't uploaded any versioned drafts to this project.
            </p>
          </div>
        )}
      </div>
    );
  }
  
  const nextVersionNumber = versions.length > 0
    ? Math.max(...versions.map(v => v.versionNumber || v.version_number || 0)) + 1
    : 1;
  
  return (
    <div className="space-y-2">
      {/* Upload button / form for owner */}
      {isOwner && !showUpload && (
        <button
          onClick={() => setShowUpload(true)}
          className="w-full flex items-center justify-center gap-2 p-3 rounded-xl border-2 border-dashed border-zinc-700/60 hover:border-purple-500/40 hover:bg-purple-500/5 text-zinc-400 hover:text-purple-300 transition-all text-sm font-medium"
        >
          <Upload className="w-4 h-4" />
          Upload New Version
        </button>
      )}
      
      {showUpload && (
        <UploadForm
          projectId={projectId}
          nextVersionNumber={nextVersionNumber}
          onSuccess={handleUploadSuccess}
          onCancel={() => setShowUpload(false)}
        />
      )}
      
      {/* Version timeline */}
      <div className="relative">
        {/* Timeline line */}
        {versions.length > 1 && (
          <div className="absolute left-[19px] top-6 bottom-6 w-px bg-gradient-to-b from-purple-500/30 via-zinc-700/30 to-transparent" />
        )}
        
        {versions.map((v, idx) => (
          <VersionRow
            key={v.id}
            version={v}
            isLatest={idx === 0}
            isExpanded={expandedId === v.id}
            onToggle={() => setExpandedId(expandedId === v.id ? null : v.id)}
            onDownload={handleDownload}
          />
        ))}
      </div>
      
      {/* Download all */}
      {versions.length > 1 && (
        <button
          onClick={handleDownloadAll}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-medium text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/40 transition-colors mt-2"
        >
          <Download className="w-3.5 h-3.5" />
          Download All Versions ({versions.length})
        </button>
      )}
    </div>
  );
};

export default VersionHistory;